import React, { useState, useEffect } from "react";
import { Link, useSearchParams } from "react-router-dom"; // <-- useSearchParams hinzugefügt
import { motion, AnimatePresence } from "framer-motion";
import { characters } from "../data/characters1";
import { books } from "../data/books";
import { Atmosphere } from "../components/Atmosphere";
import { ChevronLeft, Lock } from "lucide-react";

const filters = [
  { id: "Alle", en: "All", de: "Alle" },
  { id: "OW", en: "OB", de: "OW" }, 
  { id: "UW", en: "UB", de: "UW" }
];

export default function Characters() {
  const [currentLang, setCurrentLang] = useState(() => {
    return localStorage.getItem("project_lang") || "de";
  });

  // 1. URL-Parameter auslesen & steuern
  const [searchParams, setSearchParams] = useSearchParams();
  const bookParam = searchParams.get("book");
  
  // selectedBook ist eine Zahl oder null
  const selectedBook = bookParam ? Number(bookParam) : null;

  const [filter, setFilter] = useState("Alle");

  useEffect(() => {
    const handleLangChange = () => {
      setCurrentLang(localStorage.getItem("project_lang") || "de");
    };
    window.addEventListener("languageChange", handleLangChange);
    return () => window.removeEventListener("languageChange", handleLangChange);
  }, []);

  // Hilfsfunktion zum Auswählen/Zurücksetzen des Buches in der URL
  const handleSelectBook = (bookId) => {
    if (bookId === null) {
      setSearchParams({}); // löscht ?book=... aus der URL
    } else {
      setSearchParams({ book: bookId }); // setzt z.B. ?book=1
    }
  };

  const activeBookObj = books.find((b) => b.id === selectedBook);

  // Gefilterte Charaktere nach Buch und OW/UW-Filter
  const list = characters.filter((c) => {
    const matchesBook = selectedBook 
      ? (Array.isArray(c.books) ? c.books.includes(selectedBook) : c.book === selectedBook || (!c.book && selectedBook === 1))
      : true;
    const matchesFilter = filter === "Alle" || c.tag === filter;
    return matchesBook && matchesFilter;
  });

  return (
    <div className="pt-32 pb-24 px-6 relative" data-testid="characters-page">
      <Atmosphere variant="minimal" />
      <div className="max-w-7xl mx-auto relative z-10">
        
        {/* HEADER */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 1 }}
          className="text-center mb-12"
        >
          <p className="chinese text-[#C9A227]/70 text-2xl mb-3">人物</p>
          <h1 className="serif text-5xl lg:text-7xl text-[#E8DDC8] mb-4">
            {currentLang === "en" ? (
              <>The <span className="italic text-[#C9A227]">Courtiers</span></>
            ) : (
              <>Die <span className="italic text-[#C9A227]">Höflinge</span></>
            )}
          </h1>
          <p className="serif italic text-[#B3B6B7] text-lg max-w-2xl mx-auto">
            {selectedBook 
              ? `Band ${activeBookObj?.number}: ${activeBookObj?.title[currentLang]}`
              : (currentLang === "en" 
                ? "Select a volume to reveal the characters of that era."
                : "Wähle einen Band aus, um die Charaktere dieser Epoche zu enthüllen.")}
          </p>
        </motion.div>

        {/* ANSICHT 1: BUCHAUSWAHL */}
        {!selectedBook && (
          <motion.div 
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            className="grid grid-cols-1 gap-6 max-w-4xl mx-auto mb-16"
          >
            {books.map((book) => {
              // Hier trags du in die Klammer [ ] einfach alle IDs ein, die FREI sein sollen:!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!
              const isLocked = ![1, 2].includes(book.id);

              return (
                <div
                  key={book.id}
                  onClick={() => !isLocked && handleSelectBook(book.id)} // Klick wird ignoriert wenn gesperrt
                  className={`card-imperial p-8 relative group transition-all duration-500 overflow-hidden ${
                    isLocked 
                      ? "opacity-60 cursor-not-allowed grayscale-[30%]" 
                      : "cursor-pointer hover:scale-[1.01]"
                  }`}
                  style={{ borderColor: isLocked ? "rgba(201, 162, 39, 0.15)" : `${book.color}44` }}
                >
                  <div className="corner-orn tl" />
                  <div className="corner-orn br" />

                  {/* Farblicher Accent-Glow im Hintergrund (nur bei freigeschalteten Bänden) */}
                  {!isLocked && (
                    <div 
                      className="absolute -top-12 -right-12 w-32 h-32 rounded-full blur-3xl opacity-20 transition-opacity group-hover:opacity-40"
                      style={{ backgroundColor: book.color }}
                    />
                  )}

                  <div className="flex justify-between items-start mb-4">
                    <span 
                      className="serif text-4xl font-bold tracking-widest"
                      style={{ color: isLocked ? "#7A7C7D" : book.color }}
                    >
                      {book.number}
                    </span>

                    {/* Schloss-Symbol bei gesperrten Büchern */}
                    {isLocked && (
                      <div className="flex items-center gap-2 text-[#7A7C7D] bg-black/40 px-3 py-1 border border-[#7A7C7D]/20 rounded-full">
                        <Lock size={14} />
                        <span className="text-[10px] tracking-[0.2em] uppercase">
                          {currentLang === "en" ? "Locked" : "Gesperrt"}
                        </span>
                      </div>
                    )}
                  </div>

                  <p className="text-xs uppercase tracking-[0.2em] text-[#7A7C7D] mb-1">
                    {book.subtitle[currentLang]}
                  </p>
                  <h3 className={`serif text-2xl mb-3 transition-colors ${isLocked ? "text-[#7A7C7D]" : "text-[#E8DDC8] group-hover:text-[#C9A227]"}`}>
                    {book.title[currentLang]}
                  </h3>

                  <div className="pt-4 border-t border-[#C9A227]/15 flex justify-between items-center mt-6">
                    <span className="text-[10px] tracking-[0.2em] uppercase text-[#B3B6B7]">
                      {book.status[currentLang]}
                    </span>

                    {!isLocked && (
                      <span 
                        className="text-xs uppercase tracking-[0.2em] transition-transform group-hover:translate-x-1"
                        style={{ color: book.color }}
                      >
                        {currentLang === "en" ? "Explore →" : "Entdecken →"}
                      </span>
                    )}
                  </div>
                </div>
              );
            })}
          </motion.div>
        )}

        {/* ANSICHT 2: CHARAKTER-GRID DES GEWÄHLTEN BUCHS */}
        {selectedBook && (
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}>
            
            {/* ZURÜCK-BUTTON & FILTER BAR */}
            <div className="flex flex-col md:flex-row justify-between items-center gap-6 mb-12 border-b border-[#C9A227]/20 pb-6">
              <button
                onClick={() => handleSelectBook(null)} // <- Setzt URL zurück auf Buchübersicht
                className="btn-ghost text-xs inline-flex items-center gap-2"
              >
                <ChevronLeft size={16} />
                {currentLang === "en" ? "Back to Volumes" : "Zurück zur Bandübersicht"}
              </button>

              {/* OW / UW Filter */}
              <div className="flex gap-3">
                {filters.map((f) => {
                  const displayFilter = currentLang === "en" ? f.en : f.de;
                  return (
                    <button
                      key={f.id}
                      onClick={() => setFilter(f.id)}
                      className={`px-5 py-1.5 text-xs tracking-[0.25em] uppercase border transition-all ${
                        filter === f.id
                          ? "border-[#C9A227] text-[#C9A227] bg-[#C9A227]/10"
                          : "border-[#C9A227]/20 text-[#E8DDC8]/60 hover:border-[#C9A227]/50"
                      }`}
                    >
                      {displayFilter}
                    </button>
                  );
                })}
              </div>
            </div>

            {/* PRÜFUNG: CHARAKTERE VORHANDEN ODER LEERE SEITE */}
            {list.length === 0 ? (
              <motion.div 
                initial={{ opacity: 0, scale: 0.95 }}
                animate={{ opacity: 1, scale: 1 }}
                className="card-imperial p-12 text-center max-w-2xl mx-auto my-12 relative overflow-hidden"
              >
                <div className="corner-orn tl" />
                <div className="corner-orn tr" />
                <div className="corner-orn bl" />
                <div className="corner-orn br" />

                <p className="chinese text-5xl text-[#C9A227]/30 mb-4">封</p>
                <h3 className="serif text-2xl text-[#E8DDC8] mb-3">
                  {currentLang === "en" ? "Realm Sealed" : "Unerschlossenes Gebiet"}
                </h3>
                <p className="serif italic text-[#B3B6B7] text-base leading-relaxed">
                  {currentLang === "en" 
                    ? "You can only explore this area at a later point in time."
                    : "Diesen Bereich kannst du erst zu einem späteren Zeitpunkt erkunden."}
                </p>
              </motion.div>
            ) : (
              /* CHARAKTER KARTEN */
              <div className="grid sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
                <AnimatePresence>
                  {list.map((c, idx) => (
                    <motion.div
                      key={c.slug}
                      initial={{ opacity: 0, y: 24 }}
                      animate={{ opacity: 1, y: 0 }}
                      exit={{ opacity: 0, scale: 0.95 }}
                      transition={{ duration: 0.5, delay: idx * 0.04 }}
                    >
                      <Link to={`/charaktere/${c.slug}`} data-testid={`character-card-${c.slug}`}>
                        <div className="card-imperial p-0 overflow-hidden group">
                          <div className="aspect-[3/4] relative overflow-hidden">
                            <div
                              className="absolute inset-0"
                              style={{ background: `linear-gradient(160deg, ${c.accent}22 0%, #080808 70%)` }}
                            />
                            <img
                              src={`/characters/${c.slug}.jpg`}
                              alt={c.name}
                              loading="lazy"
                              className="absolute inset-0 w-full h-full object-cover transition-all duration-[1200ms] group-hover:scale-[1.04]"
                              style={{ filter: "saturate(0.85) contrast(1.05) brightness(0.85)" }}
                            />
                            <div
                              className="absolute inset-0"
                              style={{ background: `radial-gradient(ellipse at center, transparent 40%, rgba(8,8,8,0.55) 100%)` }}
                            />
                            <span
                              className="chinese absolute top-3 right-4 text-5xl opacity-30 group-hover:opacity-60 transition-opacity duration-700 pointer-events-none"
                              style={{ color: c.accent, textShadow: '0 0 12px rgba(0,0,0,0.6)' }}
                            >
                              {c.chinese.charAt(0)}
                            </span>
                            <div className="absolute top-3 left-3 flex items-center gap-2">
                              <span
                                className="text-[10px] tracking-[0.3em] uppercase px-2 py-1 border backdrop-blur-sm bg-black/40"
                                style={{ borderColor: c.accent, color: c.accent }}
                              >
                                {currentLang === "en" ? (c.tag === "OW" ? "OB" : "UB") : c.tag}
                              </span>
                            </div>
                            <div className="absolute bottom-0 left-0 right-0 p-5 bg-gradient-to-t from-[#080808] via-[#080808]/85 to-transparent">
                              <p className="chinese text-sm" style={{ color: c.accent }}>{c.chinese}</p>
                              <h3 className="serif text-2xl text-[#E8DDC8] leading-tight">{c.name}</h3>
                              <p className="text-xs tracking-[0.2em] uppercase text-[#7A7C7D] mt-1">
                                {c.kingdom}
                              </p>
                            </div>
                          </div>
                          <div className="p-5 border-t border-[#C9A227]/15">
                            <p className="text-sm text-[#B3B6B7]">
                              <span className="text-[#C9A227]">{currentLang === "en" ? "Power:" : "Kraft:"}</span>{" "}
                              {c.power?.[currentLang] || c.power?.["de"]}
                            </p>
                            <p className="text-sm text-[#B3B6B7] mt-1">
                              <span className="text-[#C9A227]">{currentLang === "en" ? "Age:" : "Alter:"}</span> {c.age}
                            </p>
                          </div>
                        </div>
                      </Link>
                    </motion.div>
                  ))}
                </AnimatePresence>
              </div>
            )}
          </motion.div>
        )}

      </div>
    </div>
  );
}